import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Trophy, RotateCw, Lightbulb } from 'lucide-react';
import api, { apiError } from '../../lib/api';

// SCÉNARIOS (Chatting Academy importée le 05/09 depuis hera-chatting-academy.netlify.app) :
// 54 cas réels écrits par Marlon. On lit le scénario, on écrit sa meilleure réponse,
// on reçoit une note /10 + la réponse idéale + POURQUOI elle est meilleure.
// Différence avec le site d'origine : l'IA tourne côté serveur avec la clé de l'agence
// (plus de clé API dans le navigateur de chaque chatteur) et les notes restent dans ChatHub.
export default function AcademyDrills() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState('all');
  const [courant, setCourant] = useState(null);
  const [reponse, setReponse] = useState('');
  const [ev, setEv] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [stats, setStats] = useState(null);

  const chargerStats = () => api.get('/academy/results').then(({ data }) => setStats(data)).catch(() => {});

  useEffect(() => {
    api.get('/academy/scenarios').then(({ data }) => setData(data)).catch(e => toast.error(apiError(e, 'Chargement impossible')));
    chargerStats();
  }, []);

  const pool = (data?.scenarios || []).filter(s => cat === 'all' || s.cat === cat);

  const tirer = () => {
    if (!pool.length) return;
    // On évite de retomber sur le scénario qu'on vient de faire.
    const autres = pool.filter(s => s.id !== courant?.id);
    const liste = autres.length ? autres : pool;
    setCourant(liste[Math.floor(Math.random() * liste.length)]);
    setReponse('');
    setEv(null);
  };

  const corriger = async () => {
    if (reponse.trim().length < 3) { toast.error('Écris ta réponse d\'abord'); return; }
    setEnvoi(true);
    try {
      const { data } = await api.post('/academy/evaluate', { scenario_id: courant.id, reponse });
      setEv(data);
      chargerStats();
    } catch (e) { toast.error(apiError(e, 'Correction impossible')); }
    finally { setEnvoi(false); }
  };

  const couleur = (n) => (n >= 8 ? 'text-emerald-300' : n >= 6 ? 'text-amber-300' : 'text-rose-300');

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setCat('all'); setCourant(null); setEv(null); }}
            className={`px-2.5 h-7 rounded-lg text-xs ${cat === 'all' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>
            Toutes ({data?.scenarios?.length || 0})
          </button>
          {(data?.categories || []).map(c => (
            <button key={c.cat} onClick={() => { setCat(c.cat); setCourant(null); setEv(null); }}
              className={`px-2.5 h-7 rounded-lg text-xs ${cat === c.cat ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>
              {c.label} ({c.n})
            </button>
          ))}
          <Button onClick={tirer} size="sm" className="ml-auto h-7 bg-indigo-500 hover:bg-indigo-600 text-[12px]">
            {courant ? <><RotateCw size={12} className="mr-1.5" /> Scénario suivant</> : 'Commencer'}
          </Button>
        </div>
      </div>

      {courant && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-200 border border-indigo-500/30">{courant.label}</span>
            <span className="text-slate-400">tu joues <b className="text-slate-200">{courant.modele}</b></span>
          </div>
          <div className="text-[13px] text-slate-300"><span className="text-slate-500">Situation : </span>{courant.contexte}</div>
          <div className="rounded-xl bg-white/[0.06] px-3.5 py-2.5 text-[13px] text-slate-100">
            <span className="text-[11px] text-slate-500 block mb-0.5">le fan écrit</span>
            « {courant.fan} »
          </div>

          <textarea value={reponse} onChange={e => setReponse(e.target.value)} rows={3}
            disabled={!!ev} data-testid="academy-answer"
            placeholder="Écris ta meilleure réponse, exactement comme tu l'enverrais…"
            className="w-full bg-[#18171C] border border-white/10 rounded-xl p-3 text-[13px] text-white outline-none focus:border-indigo-500/50 disabled:opacity-60" />

          {!ev ? (
            <Button onClick={corriger} disabled={envoi} className="bg-indigo-500 hover:bg-indigo-600">
              {envoi ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trophy size={14} className="mr-1.5" />}
              Me corriger
            </Button>
          ) : (
            <div className="space-y-3 border-t border-white/5 pt-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-2xl font-bold ${couleur(ev.score)}`}>{ev.score}/10</span>
                <span className="text-[13px] text-slate-200">{ev.verdict}</span>
              </div>
              {ev.good && <div className="text-[12px] text-slate-300"><span className="text-emerald-300 font-semibold">Ce qui était bien : </span>{ev.good}</div>}
              {ev.improve && <div className="text-[12px] text-slate-300"><span className="text-rose-300 font-semibold">Ce qui manquait : </span>{ev.improve}</div>}
              {ev.ideal && (
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3">
                  <div className="text-[11px] font-semibold text-emerald-300 mb-1">La réponse idéale</div>
                  <div className="text-[13px] text-slate-100 whitespace-pre-wrap">{ev.ideal}</div>
                </div>
              )}
              {ev.explanation && <div className="text-[12px] text-slate-400"><Lightbulb size={12} className="inline mr-1 text-amber-300" />{ev.explanation}</div>}
              {ev.principle && <div className="text-[12px] text-indigo-300 font-medium">À retenir : {ev.principle}</div>}
              <Button onClick={tirer} size="sm" className="bg-indigo-500 hover:bg-indigo-600 text-[12px] h-8">
                <RotateCw size={12} className="mr-1.5" /> Scénario suivant
              </Button>
            </div>
          )}
        </div>
      )}

      {stats && (stats.par_categorie || []).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-sm font-semibold text-white mb-2">
            {stats.manager ? 'Résultats de l\'équipe' : 'Mes résultats'}
          </div>
          {stats.manager && (stats.par_personne || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {stats.par_personne.map(p => (
                <span key={p.nom} className="text-[11px] px-2 py-1 rounded-lg bg-white/[0.06] text-slate-200">
                  {p.nom} · <b className={couleur(p.moyenne)}>{p.moyenne}/10</b> <span className="text-slate-500">({p.faits})</span>
                </span>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 mb-1">Moyenne par thème — les plus faibles en premier</div>
          <div className="space-y-1">
            {stats.par_categorie.map(c => (
              <div key={c.categorie} className="flex items-center gap-2 text-[12px]">
                <span className="text-slate-300 w-44 truncate">{c.categorie}</span>
                <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`h-full ${c.moyenne >= 8 ? 'bg-emerald-400' : c.moyenne >= 6 ? 'bg-amber-400' : 'bg-rose-400'}`}
                    style={{ width: `${(c.moyenne || 0) * 10}%` }} />
                </div>
                <span className={`w-14 text-right font-semibold ${couleur(c.moyenne)}`}>{c.moyenne}/10</span>
                <span className="w-10 text-right text-slate-500">({c.faits})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
